/**
 * Shared online status utilities.
 *
 * This module provides a single source of truth for determining user online status.
 * - Users are considered online if they've been active within ONLINE_THRESHOLD_MINUTES
 * - Clients refresh their auth token at REFRESH_INTERVAL_MS to keep the rolling window alive
 */

// Single source of truth for online threshold
const ONLINE_THRESHOLD_MINUTES = 5;

// Online threshold in milliseconds - derived from ONLINE_THRESHOLD_MINUTES
export const ONLINE_THRESHOLD_MS = ONLINE_THRESHOLD_MINUTES * 60 * 1000;

// How often clients should call auth.refresh to roll the token window forward
// Dev uses 10s for faster testing, prod matches online threshold (5 min)
const isDev =
  process.env.ENV === 'development' ||
  process.env.NODE_ENV === 'development' ||
  process.env.EXPO_PUBLIC_ENV === 'development';
export const REFRESH_INTERVAL_MS = isDev ? 10 * 1000 : ONLINE_THRESHOLD_MS;

/**
 * Determines if a user is online based on their activity status.
 *
 * A user is considered online if:
 * 1. isActive is true (they haven't explicitly gone offline)
 * 2. lastActive is within the ONLINE_THRESHOLD_MS window
 *
 * @param isActive - Whether the user is marked as active
 * @param lastActive - The user's last activity timestamp
 * @returns true if the user should be shown as online
 */
export const isUserOnline = (
  isActive: boolean,
  lastActive: Date | string | null
): boolean => {
  if (!isActive || !lastActive) return false;

  const lastActiveDate =
    typeof lastActive === 'string' ? new Date(lastActive) : lastActive;
  const threshold = new Date(Date.now() - ONLINE_THRESHOLD_MS);

  return lastActiveDate > threshold;
};
